/**
 * The camera as an examiner.
 *
 * This is what separates the feature from a page of text: you do the thing,
 * photograph it, and get told something specific about what you did. A tutorial
 * without a feedback loop is a recipe with more words, and we already have one.
 *
 * It is honest about its limits. The model is looking at one photo of one
 * board and it will sometimes be wrong, so the verdict is deliberately coarse —
 * good, close, off — rather than a score, and "unclear" is a first-class answer
 * rather than a failure. A number would imply a precision that isn't there.
 *
 * Nothing about the rest of the app depends on this working, exactly like
 * recognition: no key means the technique still teaches, it just can't mark.
 */

import { readVisionConfig } from "../vision/config";
import { parseLoosely } from "../vision/parseRecognition";
import type { Verdict } from "./practiceStorage";

/** Long enough for a cold model, short enough that nobody waits on it. */
const TIMEOUT_MS = 20_000;
const MAX_OBSERVATIONS = 3;
const MAX_SENTENCE = 240;

export interface Feedback {
  verdict: Verdict;
  /** What it says it can see. One to three short sentences. */
  observations: string[];
  /** The one thing to change, or null when there's nothing to say. */
  fix: string | null;
}

export type CheckOutcome =
  | { status: "ok"; feedback: Feedback }
  | { status: "off" }
  | { status: "failed"; message: string };

const VERDICTS: Verdict[] = ["good", "close", "off", "unclear"];

/** Control characters and stray whitespace that turn up inside model strings. */
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

function clean(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.replace(CONTROL_CHARS, " ").replace(/\s+/g, " ").trim();
  if (!text) return null;
  return text.length > MAX_SENTENCE ? `${text.slice(0, MAX_SENTENCE - 1)}…` : text;
}

/**
 * Turns whatever the model said into a Feedback, or null.
 *
 * Anything it can't read becomes "unclear" rather than a failure, because from
 * the user's side "I couldn't tell from that photo" and "the model waffled" are
 * the same event and want the same next action: take a better photo.
 */
export function toFeedback(payload: unknown): Feedback | null {
  if (typeof payload !== "object" || payload === null) return null;
  const record = payload as Record<string, unknown>;

  const raw = clean(record.assessment)?.toLowerCase() ?? "";
  const verdict = VERDICTS.find((option) => raw.includes(option)) ?? "unclear";

  const observations = (Array.isArray(record.observations) ? record.observations : [])
    .map(clean)
    .filter((line): line is string => line !== null)
    .slice(0, MAX_OBSERVATIONS);

  const fix = clean(record.fix);

  // With neither a description nor a suggestion there is nothing to show, and
  // an empty card is worse than an honest failure.
  if (observations.length === 0 && !fix) return null;

  return { verdict, observations, fix };
}

/**
 * Sends one photo to be marked.
 *
 * Deliberately **not cached**, unlike food recognition. The same photo means
 * the same dice, and someone re-checking is asking about a *new* attempt — a
 * cached verdict would silently tell them their second try scored exactly the
 * same as their first, which is both wrong and discouraging.
 *
 * @param photo a JPEG data URL from downscalePhoto
 * @param techniqueId names which of the server's subjects to use
 * @param looksFor the same sentence, needed only by direct mode
 */
export async function checkTechnique(
  photo: string,
  techniqueId: string,
  looksFor: string
): Promise<CheckOutcome> {
  const config = readVisionConfig();
  if (!config) return { status: "off" };

  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TIMEOUT_MS);

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (config.apiKey) headers.Authorization = `Bearer ${config.apiKey}`;

    const response = await fetch(config.endpoint, {
      method: "POST",
      signal: controller.signal,
      headers,
      // In proxy mode the client names *which* technique; the server holds
      // both the prompt and the sentence describing what to look at. Sending
      // the words from here would hand prompt text back to the caller, which
      // is the one thing the proxy exists to prevent.
      body: JSON.stringify(
        config.mode === "proxy"
          ? { photo, task: "technique", technique: techniqueId }
          : buildDirectBody(photo, looksFor, config.model)
      ),
    });

    if (!response.ok) {
      return {
        status: "failed",
        message:
          response.status === 429
            ? "That's a lot of photos — give it a few minutes."
            : "Couldn't get feedback just now. The technique is still below.",
      };
    }

    const body: unknown = await response.json();
    const text = (body as { choices?: { message?: { content?: unknown } }[] })?.choices?.[0]
      ?.message?.content;
    if (typeof text !== "string") {
      return { status: "failed", message: "Couldn't read the reply." };
    }

    const feedback = toFeedback(parseLoosely(text));
    if (!feedback) {
      return { status: "failed", message: "Couldn't tell from that photo — try another." };
    }

    return { status: "ok", feedback };
  } catch {
    return {
      status: "failed",
      message: timedOut
        ? "That took too long — try again."
        : "Couldn't reach the photo service. The technique is still below.",
    };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Direct mode only: the prompt has to be built here because there's no proxy
 * holding one. Kept beside the proxy path rather than hidden, so it is obvious
 * that this route puts the prompt in the browser — which is exactly why it
 * isn't the recommended one.
 */
function buildDirectBody(photo: string, looksFor: string, model: string) {
  return {
    model,
    messages: [
      {
        role: "user",
        content: [
          {
            type: "text",
            text: `You are a patient cooking teacher looking at a photo of a student's work.

The photo should show ${looksFor}.

Judge only what you can actually see. Be specific and kind; a beginner is
reading this. Never invent a fault you cannot see in the photo.

Reply with JSON only. No prose, no markdown, no code fences. Exactly this shape:
{"assessment": string, "observations": [string], "fix": string}

assessment: exactly one of "good", "close", "off".
observations: one to three short sentences about what you can see.
fix: the single most useful thing to change next time, or "" if it's good.

If the photo does not show what was asked for, or is too blurry or dark to
judge, use assessment "unclear" and say so in observations.`,
          },
          { type: "image_url", image_url: { url: photo } },
        ],
      },
    ],
    temperature: 0.2,
    top_p: 0.7,
    max_tokens: 512,
    stream: false,
  };
}
